import { USER_ROLES } from './narrow';
import type { SystemUser, UserRole } from '@micarahr/shared-types';
import {
  insert,
  paginate,
  readAll,
  readOne,
  update,
  type Page,
  type PageRequest,
} from './collections';
import { errors, runtime, serviceCall } from './runtime';
import { env } from '../config/env';
import type { EmploymentTypeValue } from '../lib/people-labels';
import { apiEmployeeService } from './directory.api';
import { apiOrganizationService } from './organization.api';
import { apiUserAdminService, type CreatedLogin } from './access.api';
import { recordAudit, type AuditActor } from './audit';
import { requireSession } from './auth.mock';
import { nextId } from './ids';
import type { AdminAttendanceRecord } from './seed';
import { employmentStanding } from '../lib/separation';

/**
 * Employee administration.
 *
 * The service, not the screen, decides what a caller may see. A department head
 * asking for the directory gets their own department; the same call from an HR
 * administrator returns the company. Filtering in the component would leave the
 * full dataset one devtools inspection away.
 */

export interface AdminEmployee {
  id: string;
  employeeNumber: string;
  firstName: string;
  middleName: string;
  lastName: string;
  preferredName: string;
  fullName: string;
  workEmail: string;
  personalEmail: string;
  mobileNumber: string;
  address: string;
  birthDate: string;
  gender: string;
  civilStatus: string;
  departmentId: string;
  departmentName: string;
  teamId: string;
  teamName: string;
  positionId: string;
  positionTitle: string;
  jobLevelId: string;
  jobLevelName: string;
  managerEmployeeId: string | null;
  managerName: string | null;
  workLocationId: string;
  workLocationName: string;
  /**
   * The server's employment type. "contractual" is the prototype's word for
   * `fixed_term`, still found in the demonstration data.
   */
  employmentType: EmploymentTypeValue | 'contractual';
  employmentStatus: 'active' | 'probationary' | 'on_leave' | 'inactive' | 'resigned';
  dateHired: string;
  probationEndDate: string | null;
  /**
   * The day a regular employee was confirmed, where the record says. Only the
   * detail endpoint returns it.
   */
  regularSince?: string | null;
  contractEndDate: string | null;
  costCenterId: string;
  shiftId: string;
  monthlyRate: number;
  sssNumber: string;
  philHealthNumber: string;
  pagIbigNumber: string;
  tinNumber: string;
  bankAccount: string;
  emergencyContactName: string;
  emergencyContactNumber: string;
  emergencyContactRelation: string;
  dependents: { name: string; relation: string; birthDate: string }[];
  avatarUrl: string | null;
  /*
   * Read from the detail endpoint only. Optional so the list rows, which do
   * not carry them, stay honest about not knowing.
   */
  payrollGroupId?: string | null;
  payrollGroupName?: string | null;
  costCenterName?: string | null;
  shiftName?: string | null;
  /** A last working day agreed but not yet reached. */
  separationDate?: string | null;
  /** Whether this person already has a sign-in; null when not known. */
  hasLogin?: boolean | null;
}

export interface EmployeeFilter extends PageRequest {
  departmentId?: string;
  teamId?: string;
  positionId?: string;
  managerEmployeeId?: string;
  workLocationId?: string;
  /**
   * A status, or a group of them, by the one rule the badges use
   * (lib/separation): `current` is the present workforce (started, not past
   * their last working day) — what the rail's headcount counts; `separating`
   * is the part of it with a last working day of today or later; `separated`
   * is everybody past their last working day; `upcoming` is a hire whose
   * start date has not arrived; `all` is every record. A single status is
   * counted among the current workforce not separating, as the rail counts it.
   */
  employmentStatus?: AdminEmployee['employmentStatus'] | EmployeeStatusGroup | 'regular' | 'all';
  employmentType?: AdminEmployee['employmentType'] | 'all';
  savedView?: SavedEmployeeView;
}

export type EmployeeStatusGroup = 'current' | 'separating' | 'separated' | 'upcoming';

export type SavedEmployeeView =
  | 'all'
  | 'new_hires'
  | 'probationary'
  | 'regular'
  | 'contractual'
  | 'on_leave'
  | 'inactive'
  | 'missing_documents'
  | 'no_manager'
  | 'contract_expiring';

export interface DirectoryScope {
  /** Empty means company-wide. */
  departmentIds: string[];
  /**
   * Whether the viewer holds `report.view`. The API's summary asks the
   * workforce report first and counts the visible directory otherwise; a
   * recruiter asking the report anyway drew a refused request on every visit.
   */
  reportsAllowed?: boolean;
}

export interface EmployeeSummary {
  total: number;
  active: number;
  probationary: number;
  onLeave: number;
  inactive: number;
  /** The current workforce with a last working day of today or later. */
  separating: number;
  /** People past their last working day, or withdrawn before starting. */
  separated: number;
  /**
   * Hired, start date still to come. With the current workforce and the
   * separated, the whole of "Everyone".
   */
  notStarted: number;
  /** Null when the data source cannot count it honestly — see the adapter. */
  newHiresThisMonth: number | null;
}

function applyScope(rows: AdminEmployee[], scope: DirectoryScope): AdminEmployee[] {
  if (scope.departmentIds.length === 0) return rows;
  return rows.filter((row) => scope.departmentIds.includes(row.departmentId));
}

function applySavedView(
  rows: AdminEmployee[],
  view: SavedEmployeeView | undefined,
): AdminEmployee[] {
  if (!view || view === 'all') return rows;
  const now = new Date();

  switch (view) {
    case 'new_hires':
      return rows.filter((row) => {
        const hired = new Date(row.dateHired);
        const days = (now.getTime() - hired.getTime()) / 86_400_000;
        return days <= 90;
      });
    case 'probationary':
      return rows.filter((row) => row.employmentStatus === 'probationary');
    case 'regular':
      return rows.filter((row) => row.employmentType === 'regular');
    case 'contractual':
      return rows.filter(
        (row) => row.employmentType === 'contractual' || row.employmentType === 'fixed_term',
      );
    case 'on_leave':
      return rows.filter((row) => row.employmentStatus === 'on_leave');
    case 'inactive':
      return rows.filter(
        (row) => row.employmentStatus === 'inactive' || row.employmentStatus === 'resigned',
      );
    case 'no_manager':
      return rows.filter((row) => row.managerEmployeeId === null);
    case 'contract_expiring':
      return rows.filter((row) => {
        if (!row.contractEndDate) return false;
        const days = (new Date(row.contractEndDate).getTime() - now.getTime()) / 86_400_000;
        return days >= 0 && days <= 90;
      });
    case 'missing_documents': {
      const documents = readAll<{ employeeId: string }>('documents');
      const counts = new Map<string, number>();
      for (const document of documents) {
        counts.set(document.employeeId, (counts.get(document.employeeId) ?? 0) + 1);
      }
      // Six document types are expected; anything less is an incomplete file.
      return rows.filter((row) => (counts.get(row.id) ?? 0) < 6);
    }
    default:
      return rows;
  }
}

const mockEmployeeService = {
  async list(filter: EmployeeFilter, scope: DirectoryScope): Promise<Page<AdminEmployee>> {
    return serviceCall('employeeService', () => {
      let rows = applyScope(readAll<AdminEmployee>('employees'), scope);
      rows = applySavedView(rows, filter.savedView);

      if (filter.departmentId)
        rows = rows.filter((row) => row.departmentId === filter.departmentId);
      if (filter.teamId) rows = rows.filter((row) => row.teamId === filter.teamId);
      if (filter.positionId) rows = rows.filter((row) => row.positionId === filter.positionId);
      if (filter.managerEmployeeId) {
        rows = rows.filter((row) => row.managerEmployeeId === filter.managerEmployeeId);
      }
      if (filter.workLocationId) {
        rows = rows.filter((row) => row.workLocationId === filter.workLocationId);
      }
      const status = filter.employmentStatus;
      const phase = (row: AdminEmployee) => employmentStanding(row).phase;
      if (status === 'current') {
        rows = rows.filter((row) => ['employed', 'separating'].includes(phase(row)));
      } else if (status === 'separating') {
        rows = rows.filter((row) => phase(row) === 'separating');
      } else if (status === 'separated') {
        rows = rows.filter((row) => ['separated', 'withdrawn'].includes(phase(row)));
      } else if (status === 'upcoming') {
        rows = rows.filter((row) => phase(row) === 'not_started');
      } else if (status === 'regular') {
        rows = rows.filter((row) => row.employmentStatus === 'active');
      } else if (status && status !== 'all') {
        rows = rows.filter((row) => row.employmentStatus === status);
      }
      if (filter.employmentType && filter.employmentType !== 'all') {
        rows = rows.filter((row) => row.employmentType === filter.employmentType);
      }

      return paginate(
        rows as unknown as Record<string, unknown>[],
        { sortBy: 'fullName', ...filter },
        (row) =>
          `${row.fullName} ${row.employeeNumber} ${row.workEmail} ${row.positionTitle} ${row.departmentName}`,
      ) as unknown as Page<AdminEmployee>;
    });
  },

  async get(id: string, scope: DirectoryScope): Promise<AdminEmployee | null> {
    return serviceCall('employeeService', () => {
      const employee = readOne<AdminEmployee>('employees', id);
      if (!employee) return null;
      if (scope.departmentIds.length > 0 && !scope.departmentIds.includes(employee.departmentId)) {
        throw errors.permissionDenied('employee.view_all');
      }
      return employee;
    });
  },

  async summary(scope: DirectoryScope): Promise<EmployeeSummary> {
    return serviceCall('employeeService', () => {
      const rows = applyScope(readAll<AdminEmployee>('employees'), scope);
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      return {
        total: rows.filter((row) =>
          ['employed', 'separating'].includes(employmentStanding(row).phase),
        ).length,
        active: rows.filter((row) => row.employmentStatus === 'active').length,
        probationary: rows.filter((row) => row.employmentStatus === 'probationary').length,
        onLeave: rows.filter((row) => row.employmentStatus === 'on_leave').length,
        inactive: rows.filter((row) => row.employmentStatus === 'inactive').length,
        separating: rows.filter((row) => employmentStanding(row).phase === 'separating').length,
        notStarted: rows.filter((row) => employmentStanding(row).phase === 'not_started').length,
        separated: rows.filter((row) =>
          ['separated', 'withdrawn'].includes(employmentStanding(row).phase),
        ).length,
        // Started this month: a start date from the 1st up to today, not later.
        newHiresThisMonth: rows.filter(
          (row) => new Date(row.dateHired) >= monthStart && new Date(row.dateHired) <= now,
        ).length,
      };
    });
  },

  /** Attendance history for the employee workspace's Attendance tab. */
  async attendanceFor(employeeId: string, limit = 30): Promise<AdminAttendanceRecord[]> {
    return serviceCall('employeeService', () =>
      readAll<AdminAttendanceRecord>('attendanceRecords')
        .filter((record) => record.employeeId === employeeId)
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, limit),
    );
  },

  async create(
    input: Omit<AdminEmployee, 'id' | 'fullName' | 'employeeNumber'> & { employeeNumber?: string },
    actor: AuditActor,
  ): Promise<AdminEmployee> {
    return serviceCall('employeeService', async () => {
      const rows = readAll<AdminEmployee>('employees');

      const employeeNumber =
        input.employeeNumber ??
        `MTC-${new Date().getFullYear()}-${String(rows.length + 1).padStart(4, '0')}`;

      if (rows.some((row) => row.employeeNumber === employeeNumber)) {
        throw errors.conflict(`Employee number ${employeeNumber} is already in use.`);
      }
      if (rows.some((row) => row.workEmail.toLowerCase() === input.workEmail.toLowerCase())) {
        throw errors.conflict(`${input.workEmail} is already assigned to another employee.`);
      }

      const employee: AdminEmployee = {
        ...input,
        id: nextId('emp'),
        employeeNumber,
        fullName: `${input.firstName} ${input.middleName} ${input.lastName}`
          .replace(/\s+/g, ' ')
          .trim(),
      };

      const { insert } = await import('./collections');
      await insert<AdminEmployee>('employees', employee);

      await recordAudit({
        actor,
        module: 'employees',
        action: 'employee.created',
        summary: `Created employee record for ${employee.fullName}`,
        recordType: 'employee',
        recordId: employee.id,
        recordLabel: employee.employeeNumber,
        subjectEmployeeId: employee.id,
        after: { fullName: employee.fullName, positionTitle: employee.positionTitle },
        severity: 'notice',
      });

      return employee;
    });
  },

  async update(
    id: string,
    changes: Partial<AdminEmployee>,
    actor: AuditActor,
    reason?: string,
  ): Promise<AdminEmployee> {
    return serviceCall('employeeService', async () => {
      const before = readOne<AdminEmployee>('employees', id);
      if (!before) throw errors.notFound('employee');

      const after = await update<AdminEmployee>(
        'employees',
        id,
        (row) => {
          const next = { ...row, ...changes };
          next.fullName = `${next.firstName} ${next.middleName} ${next.lastName}`
            .replace(/\s+/g, ' ')
            .trim();
          return next;
        },
        'employee',
      );

      const beforeMap = before as unknown as Record<string, unknown>;
      const afterMap = after as unknown as Record<string, unknown>;
      const changed = Object.keys(changes).filter((key) => beforeMap[key] !== afterMap[key]);

      await recordAudit({
        actor,
        module: 'employees',
        action: 'employee.updated',
        summary: `Updated ${after.fullName} (${changed.join(', ') || 'no visible change'})`,
        recordType: 'employee',
        recordId: id,
        recordLabel: after.employeeNumber,
        subjectEmployeeId: id,
        before: Object.fromEntries(changed.map((key) => [key, beforeMap[key]])),
        after: Object.fromEntries(changed.map((key) => [key, afterMap[key]])),
        metadata: reason ? { reason } : {},
        severity: 'notice',
      });

      return after;
    });
  },

  /**
   * Change employment status.
   *
   * Separate from `update` because a status change is a decision with a reason
   * and an effective date, and the audit entry has to say which — "employee
   * updated" tells a later reader nothing about why someone became inactive.
   */
  async changeEmploymentStatus(
    id: string,
    status: AdminEmployee['employmentStatus'],
    reason: string,
    actor: AuditActor,
  ): Promise<AdminEmployee> {
    return serviceCall('employeeService', async () => {
      if (!reason.trim()) {
        throw errors.validation('A reason is required when changing employment status.');
      }
      const before = readOne<AdminEmployee>('employees', id);
      if (!before) throw errors.notFound('employee');

      const after = await update<AdminEmployee>(
        'employees',
        id,
        (row) => ({ ...row, employmentStatus: status }),
        'employee',
      );

      await recordAudit({
        actor,
        module: 'employees',
        action: 'employee.employment_status_changed',
        summary: `Changed ${after.fullName} from ${before.employmentStatus} to ${status}`,
        recordType: 'employee',
        recordId: id,
        recordLabel: after.employeeNumber,
        subjectEmployeeId: id,
        before: { employmentStatus: before.employmentStatus },
        after: { employmentStatus: status },
        metadata: { reason },
        severity: 'sensitive',
      });

      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* Organization                                                                */
/* -------------------------------------------------------------------------- */

export interface Department {
  id: string;
  code: string;
  name: string;
  headEmployeeId: string | null;
}
export interface Team {
  id: string;
  name: string;
  departmentId: string;
}
export interface Position {
  id: string;
  title: string;
  departmentId: string;
  jobLevelId: string;
}
export interface JobLevel {
  id: string;
  name: string;
  rank: number;
}
export interface WorkLocation {
  id: string;
  name: string;
  kind: 'office' | 'branch' | 'remote' | 'field_site' | 'client_site';
  address: string;
  latitude: number | null;
  longitude: number | null;
  geofenceRadiusMeters: number | null;
  active: boolean;
}
export interface CostCenter {
  id: string;
  code: string;
  name: string;
  departmentId: string;
}
/** Who is paid together, on one schedule. An employee without one is in no pay run. */
export interface PayrollGroup {
  id: string;
  code: string;
  name: string;
  payFrequency: string;
}

const mockOrganizationService = {
  async departments(): Promise<Department[]> {
    return serviceCall('organizationService', () => readAll<Department>('departments'));
  },
  async teams(): Promise<Team[]> {
    return serviceCall('organizationService', () => readAll<Team>('teams'));
  },
  async positions(): Promise<Position[]> {
    return serviceCall('organizationService', () => readAll<Position>('positions'));
  },
  async jobLevels(): Promise<JobLevel[]> {
    return serviceCall('organizationService', () => readAll<JobLevel>('jobLevels'));
  },
  async workLocations(): Promise<WorkLocation[]> {
    return serviceCall('organizationService', () => readAll<WorkLocation>('workLocations'));
  },
  async costCenters(): Promise<CostCenter[]> {
    return serviceCall('organizationService', () => readAll<CostCenter>('costCenters'));
  },
  /** The prototype pays everybody in one group; its periods all name it. */
  async payrollGroups(): Promise<PayrollGroup[]> {
    return serviceCall('organizationService', () => [
      {
        id: 'grp_monthly',
        code: 'MONTHLY',
        name: 'Monthly — All employees',
        payFrequency: 'monthly',
      },
    ]);
  },

  /** Department rows with the headcount and head each one needs in the directory. */
  async departmentOverview() {
    return serviceCall('organizationService', () => {
      const employees = readAll<AdminEmployee>('employees');
      const teams = readAll<Team>('teams');
      return readAll<Department>('departments').map((department) => {
        const members = employees.filter((employee) => employee.departmentId === department.id);
        const head = employees.find((employee) => employee.id === department.headEmployeeId);
        return {
          ...department,
          headName: head?.fullName ?? null,
          headLeftName: null as string | null,
          headLeavingOn: null as string | null,
          headcount: members.length,
          activeCount: members.filter((member) => member.employmentStatus === 'active').length,
          teamCount: teams.filter((team) => team.departmentId === department.id).length,
        };
      });
    });
  },

  async createDepartment(
    input: { code: string; name: string; headEmployeeId: string | null },
    actor: AuditActor,
  ): Promise<Department> {
    return serviceCall('organizationService', async () => {
      const rows = readAll<Department>('departments');
      if (rows.some((row) => row.code.toLowerCase() === input.code.toLowerCase())) {
        throw errors.conflict(`Department code ${input.code} is already in use.`);
      }
      const department: Department = { id: nextId('dept'), ...input };
      const { insert } = await import('./collections');
      await insert('departments', department);
      await recordAudit({
        actor,
        module: 'organization',
        action: 'department.created',
        summary: `Created the ${department.name} department`,
        recordType: 'department',
        recordId: department.id,
        recordLabel: department.name,
        after: { ...department },
        severity: 'notice',
      });
      return department;
    });
  },

  async updateDepartment(
    id: string,
    changes: { name?: string; code?: string; headEmployeeId?: string | null },
    actor: AuditActor,
  ): Promise<Department> {
    return serviceCall('organizationService', async () => {
      const before = readOne<Department>('departments', id);
      if (!before) throw errors.notFound('department');

      if (changes.code && changes.code !== before.code) {
        const clash = readAll<Department>('departments').some(
          (row) => row.id !== id && row.code.toLowerCase() === changes.code!.toLowerCase(),
        );
        if (clash) throw errors.conflict(`Department code ${changes.code} is already in use.`);
      }

      const after = await update<Department>(
        'departments',
        id,
        (row) => ({ ...row, ...changes }),
        'department',
      );

      await recordAudit({
        actor,
        module: 'organization',
        action: 'department.updated',
        summary: `Updated the ${after.name} department`,
        recordType: 'department',
        recordId: id,
        recordLabel: after.name,
        before: { ...before },
        after: { ...after },
        severity: 'notice',
      });

      return after;
    });
  },

  /**
   * Remove a department.
   *
   * Refused while anyone is still assigned to it. A department that disappears
   * from under its employees leaves records pointing at nothing, and there is
   * no backend to repair them from.
   */
  async deleteDepartment(id: string, actor: AuditActor): Promise<void> {
    return serviceCall('organizationService', async () => {
      const department = readOne<Department>('departments', id);
      if (!department) throw errors.notFound('department');

      const assigned = readAll<AdminEmployee>('employees').filter(
        (employee) => employee.departmentId === id,
      );
      if (assigned.length > 0) {
        await recordAudit({
          actor,
          module: 'organization',
          action: 'department.delete_blocked',
          summary: `Blocked removing ${department.name} — ${assigned.length} employee(s) still assigned`,
          recordType: 'department',
          recordId: id,
          recordLabel: department.name,
          outcome: 'blocked',
          severity: 'notice',
        });
        throw errors.precondition(
          `${assigned.length} employee(s) are still assigned to ${department.name}. Move them to another department first.`,
        );
      }

      const { remove } = await import('./collections');
      await remove('departments', id);

      await recordAudit({
        actor,
        module: 'organization',
        action: 'department.deleted',
        summary: `Removed the ${department.name} department`,
        recordType: 'department',
        recordId: id,
        recordLabel: department.name,
        before: { ...department },
        severity: 'critical',
      });
    });
  },

  async updateWorkLocation(
    id: string,
    changes: Partial<WorkLocation>,
    actor: AuditActor,
    reason?: string,
  ): Promise<WorkLocation> {
    return serviceCall('organizationService', async () => {
      const before = readOne<WorkLocation>('workLocations', id);
      if (!before) throw errors.notFound('work location');
      const after = await update<WorkLocation>(
        'workLocations',
        id,
        (row) => ({ ...row, ...changes }),
        'work location',
      );
      const geofenceChanged = before.geofenceRadiusMeters !== after.geofenceRadiusMeters;
      await recordAudit({
        actor,
        module: 'organization',
        action: geofenceChanged ? 'work_location.geofence_changed' : 'work_location.updated',
        summary: geofenceChanged
          ? `Changed the geofence for ${after.name} to ${after.geofenceRadiusMeters ?? 0} m${reason ? ` — ${reason}` : ''}`
          : `Updated ${after.name}`,
        recordType: 'work_location',
        recordId: id,
        recordLabel: after.name,
        before: { ...before },
        after: { ...after },
        // A geofence change silently alters what counts as valid attendance for
        // everyone assigned to the site, so it is never a routine edit.
        severity: geofenceChanged ? 'critical' : 'notice',
      });
      return after;
    });
  },
};

/* -------------------------------------------------------------------------- */
/* System users                                                                */
/* -------------------------------------------------------------------------- */

const mockUserAdminService = {
  async list(filter: PageRequest & { status?: string }): Promise<Page<SystemUser>> {
    return serviceCall('userAdminService', () => {
      let rows = readAll<SystemUser>('systemUsers');
      if (filter.status && filter.status !== 'all') {
        rows = rows.filter((row) => row.status === filter.status);
      }
      return paginate(
        rows as unknown as Record<string, unknown>[],
        { sortBy: 'displayName', ...filter },
        (row) => `${row.displayName} ${row.email} ${(row.roles as string[]).join(' ')}`,
      ) as unknown as Page<SystemUser>;
    });
  },

  async summary() {
    return serviceCall('userAdminService', () => {
      const rows = readAll<SystemUser>('systemUsers');
      return {
        total: rows.length,
        active: rows.filter((row) => row.status === 'active').length,
        invited: rows.filter((row) => row.status === 'invited').length,
        passwordPending: rows.filter((row) => row.status === 'password_pending').length,
        suspended: rows.filter((row) => row.status === 'suspended').length,
        deactivated: rows.filter((row) => row.status === 'deactivated').length,
        /*
         * The prototype has no credential store to count, so this stays null
         * here — a screen that reads null says "not available" rather than
         * printing a zero that looks like a finding. In API mode the server
         * reports a real figure.
         */
        mfaEnabled: null as number | null,
      };
    });
  },

  /**
   * Administrators who can still administer.
   *
   * Used by the guard below. A role that is present on a deactivated account
   * does not count — the question is who could sign in and fix things.
   */
  activeAdministrators(excludeUserId?: string): SystemUser[] {
    return readAll<SystemUser>('systemUsers').filter(
      (user) =>
        user.id !== excludeUserId &&
        user.status === 'active' &&
        user.roles.some(
          (role) => role === 'super_administrator' || role === 'company_administrator',
        ),
    );
  },

  /**
   * Change an account's state.
   *
   * Takes a reason rather than an actor. The actor comes from the session —
   * passing one from a call site was a forgotten argument away from an
   * unattributed change — and the reason comes from the person, because "who
   * deactivated the payroll administrator in March and why" is the question an
   * audit asks, and half an answer names a suspect without a motive.
   */
  async setStatus(
    id: string,
    status: 'active' | 'suspended' | 'deactivated',
    reason: string,
  ): Promise<SystemUser> {
    return serviceCall('userAdminService', async () => {
      const session = requireSession();
      const actor: AuditActor = {
        userId: session.userId,
        name: session.displayName,
        roles: session.roles,
      };
      const before = readOne<SystemUser>('systemUsers', id);
      if (!before) throw errors.notFound('user');

      const isAdministrator = before.roles.some(
        (role) => role === 'super_administrator' || role === 'company_administrator',
      );

      // The lockout guard. Deactivating the last administrator leaves nobody who
      // can grant the role back, and no backend to fix it from.
      if (status !== 'active' && isAdministrator) {
        if (mockUserAdminService.activeAdministrators(id).length === 0) {
          await recordAudit({
            actor,
            module: 'users',
            action: 'user.deactivate_blocked',
            summary: `Blocked deactivating ${before.displayName} — the last active administrator`,
            recordType: 'user',
            recordId: id,
            recordLabel: before.email,
            outcome: 'blocked',
            severity: 'critical',
          });
          throw errors.precondition(
            'This is the last active administrator. Grant the administrator role to another active account first, or nobody will be able to administer MicaraHR.',
          );
        }
        if (id === actor.userId) {
          throw errors.precondition(
            'You cannot deactivate your own account while signed in to it.',
          );
        }
      }

      const after = await update<SystemUser>(
        'systemUsers',
        id,
        (row) => ({ ...row, status, updatedAt: runtime.now() }),
        'user',
      );

      await recordAudit({
        actor,
        module: 'users',
        action: status === 'active' ? 'user.activated' : 'user.deactivated',
        summary: `${status === 'active' ? 'Activated' : 'Deactivated'} ${after.displayName}`,
        recordType: 'user',
        recordId: id,
        recordLabel: after.email,
        before: { status: before.status },
        after: { status },
        metadata: { reason },
        severity: 'critical',
      });

      return after;
    });
  },

  /**
   * Replace an account's roles.
   *
   * Takes plain keys because the screen sends platform roles and the
   * account's custom role keys together (the API replaces the whole list).
   * The prototype only models platform roles, so custom keys are set aside.
   */
  async setRoles(id: string, roleKeys: string[], reason: string): Promise<SystemUser> {
    const roles = roleKeys.filter((key): key is SystemUser['roles'][number] =>
      (USER_ROLES as readonly string[]).includes(key),
    );
    return serviceCall('userAdminService', async () => {
      const session = requireSession();
      const actor: AuditActor = {
        userId: session.userId,
        name: session.displayName,
        roles: session.roles,
      };
      const before = readOne<SystemUser>('systemUsers', id);
      if (!before) throw errors.notFound('user');

      const wasAdministrator = before.roles.some(
        (role) => role === 'super_administrator' || role === 'company_administrator',
      );
      const stillAdministrator = roles.some(
        (role) => role === 'super_administrator' || role === 'company_administrator',
      );

      if (
        wasAdministrator &&
        !stillAdministrator &&
        mockUserAdminService.activeAdministrators(id).length === 0
      ) {
        await recordAudit({
          actor,
          module: 'users',
          action: 'user.role_change_blocked',
          summary: `Blocked removing the administrator role from ${before.displayName} — the last one`,
          recordType: 'user',
          recordId: id,
          recordLabel: before.email,
          outcome: 'blocked',
          severity: 'critical',
        });
        throw errors.precondition(
          'This is the last account with an administrator role. Assign it to another active account first.',
        );
      }

      const after = await update<SystemUser>(
        'systemUsers',
        id,
        (row) => ({ ...row, roles, updatedAt: runtime.now() }),
        'user',
      );

      await recordAudit({
        actor,
        module: 'users',
        action: 'user.roles_changed',
        summary: `Changed roles for ${after.displayName} to ${roles.join(', ')}`,
        recordType: 'user',
        recordId: id,
        recordLabel: after.email,
        before: { roles: before.roles },
        after: { roles },
        metadata: { reason },
        severity: 'critical',
      });

      return after;
    });
  },

  /**
   * Create a sign-in for an employee, in the browser-only store.
   *
   * The prototype has no credentials to hash, so the password is only shown;
   * the API adapter is the one that matters.
   */
  async createLogin(
    employeeId: string,
    roles: string[],
    reason: string,
    // The prototype sends no mail; the parameter keeps one signature for both.
    _sendInvitation = false,
  ): Promise<CreatedLogin> {
    return serviceCall('userAdminService', async () => {
      requireSession();
      if (reason.trim().length < 10) throw errors.validation('Give a reason of a sentence or so.');
      const employee = readOne<AdminEmployee>('employees', employeeId);
      if (!employee) throw errors.notFound('employee');
      if (readAll<SystemUser>('systemUsers').some((user) => user.employeeId === employeeId)) {
        throw errors.conflict(`${employee.fullName} already has a sign-in.`);
      }
      const now = runtime.now();
      const user: SystemUser = {
        id: nextId('usr'),
        employeeId,
        displayName: employee.fullName,
        email: employee.workEmail,
        roles: roles as UserRole[],
        customRoleIds: [],
        status: 'invited',
        scopedDepartmentIds: [],
        lastSignInAt: null,
        mfaEnabled: null,
        requirePasswordChange: true,
        lastMobileSignInAt: null,
        createdAt: now,
        updatedAt: now,
      };
      await insert('systemUsers', user);
      return {
        user,
        temporaryPassword: `Temp${Math.random().toString(36).slice(2, 10)}9`,
        invitationSent: false,
      };
    });
  },

  /** A new temporary password, in the browser-only store. See the API adapter. */
  async issueTemporaryPassword(
    id: string,
    reason: string,
  ): Promise<{ user: SystemUser; temporaryPassword: string }> {
    return serviceCall('userAdminService', async () => {
      requireSession();
      if (reason.trim().length < 10) throw errors.validation('Give a reason of a sentence or so.');
      const user = await update<SystemUser>(
        'systemUsers',
        id,
        (row) => ({ ...row, requirePasswordChange: true, updatedAt: runtime.now() }),
        'user',
      );
      return { user, temporaryPassword: `Temp${Math.random().toString(36).slice(2, 10)}9` };
    });
  },
};

/**
 * The directory adapter selector.
 *
 * Spread over the mock so the prototype's remaining helpers — summary, saved
 * views, the org-chart derivations — stay reachable while the list, detail and
 * write paths come from the server. A screen calling `summary()` must not find
 * it missing.
 */
export const employeeService =
  env().dataSource === 'api'
    ? { ...mockEmployeeService, ...apiEmployeeService }
    : mockEmployeeService;

/**
 * User administration.
 *
 * The whole surface comes from the API when connected — including the last-
 * administrator guard, which is the one rule that must not have two
 * implementations. The prototype's copy stays for the mock data source and is
 * not consulted here.
 *
 * `activeAdministrators` is deliberately not carried over. It answers "who
 * could still administer" from a browser cache, and the server answers the
 * same question inside the transaction that does the write. Two answers to
 * that question is one too many.
 */
export const userAdminService: typeof mockUserAdminService =
  env().dataSource === 'api'
    ? { ...mockUserAdminService, ...apiUserAdminService }
    : mockUserAdminService;

/**
 * Organisation structure.
 *
 * The registries come from the API; the prototype's derived views — the
 * department overview, the org chart, the vacancy counts — stay beneath until
 * each has a server endpoint to read.
 */
/*
 * Annotated with the mock's type rather than inferred.
 *
 * A spread of two objects whose methods have identical signatures infers as a
 * union of function types, and TanStack Query rejects
 * `() => Promise<T> | Promise<T>` as a query function. Naming the type keeps
 * the merged object exactly the shape every screen already consumes.
 */
export const organizationService: typeof mockOrganizationService =
  env().dataSource === 'api'
    ? { ...mockOrganizationService, ...apiOrganizationService }
    : mockOrganizationService;
